import { inject, Injectable } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { AuthUtils } from '../auth/auth.utils';
import { Router } from '@angular/router';
import { NavigationHistoryStore } from './navigation.store';
import { debugLog } from 'app/shared/utils/debug-logger';

declare global {
  interface Window {
    flutter_inappwebview?: {
      callHandler: (handlerName: string, ...args: any[]) => Promise<any>;
    };
  }
}

@Injectable({ providedIn: 'root' })
export class EmbeddedServiceService {
  private authService = inject(AuthService);
  private router = inject(Router);
  private navigationHistoryStore = inject(NavigationHistoryStore);

  constructor() {
    this.setupFlutterCommunication();
  }

  private setupFlutterCommunication(): void {
    // listen to Flutter ready event
    window.addEventListener('flutterInAppWebViewPlatformReady', event => {
      debugLog('Embedded', 'flutterInAppWebViewPlatformReady');

      this.registerHandlers();
    });

    if (window.flutter_inappwebview) {
      debugLog('Embedded', 'Flutter InAppWebView ready');

      // send accessToken to Flutter when app is ready
      const accessTokenResult = this.getAccessTokenResult();
      window.flutter_inappwebview.callHandler('accessToken', accessTokenResult);

      this.registerHandlers();
    }
  }

  private registerHandlers(): void {
    // Logout handler
    this.registerHandler('logoutHandler', async () => {
      debugLog('Embedded', 'logout triggered');
      try {
        this.router.navigate(['/sign-out']);

        return { success: true, message: 'Logout successful' };
      } catch (error) {
        console.error('Logout failed:', error);
        return { success: false, message: 'Logout failed' };
      }
    });

    // AccessToken handler
    this.registerHandler('accessTokenHandler', async () => {
      debugLog('Embedded', 'accessToken triggered');
      return this.getAccessTokenResult();
    });

    // navigate to url handler
    this.registerHandler('navigateToUrlHandler', async (url: string) => {
      debugLog('Embedded', 'navigateToUrl triggered', url);
      this.router.navigate([url]);
      return { success: true, message: 'Navigated to ' + url };
    });

    // navigate back handler
    this.registerHandler('navigateBackHandler', async () => {
      debugLog('Embedded', 'navigateBack triggered');

      // Check if we can go back using the NavigationHistoryStore
      const canGoBack = this.navigationHistoryStore.canGoBack();
      const message = canGoBack ? 'Navigated back' : 'No history to go back to';

      if (canGoBack) {
        this.navigationHistoryStore.goBack();
      }

      return { success: true, message, canGoBack };
    });

    // Log available functions after registration
    debugLog('Embedded', 'Available handlers:', {
      logout: typeof (window as any).logoutHandler === 'function',
      accessToken: typeof (window as any).accessTokenHandler === 'function',
    });
  }

  private getAccessTokenResult() {
    let accessTokenResult;

    try {
      accessTokenResult = {
        success: true,
        accessToken: this.authService.accessToken,
        expirationDate: AuthUtils.getTokenExpirationDate(this.authService.accessToken),
      };
    } catch (error) {
      console.error('Logout failed:', error);
      accessTokenResult = { success: false, message: 'failed to get accesstoken' };
    }
    return accessTokenResult;
  }

  private registerHandler(handlerName: string, callback: (...args: any[]) => Promise<any>): void {
    if (!window.flutter_inappwebview) {
      console.warn(`Flutter InAppWebView not available, couldn't register ${handlerName}`);
      return;
    }

    // Create a global function that Flutter can call
    (window as any)[handlerName] = async (...args: any[]) => {
      try {
        const result = await callback(...args);
        debugLog('Embedded', `${handlerName} result from Flutter:`, result);
        // send result to Flutter
        window.flutter_inappwebview!.callHandler(handlerName, result);
      } catch (error) {
        console.error(`Error in ${handlerName}:`, error);
        return { success: false, message: `${handlerName} failed`, error: error?.message };
      }
    };
  }
}
